import axios from 'axios';
import { normalizeApiBaseUrl } from '../utils/apiBase';

// Base API configuration
const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

// Separate axios instance for the refresh call to avoid triggering the interceptor recursively
const refreshApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // CRITICAL: Sends session cookies (sessionid + csrftoken)
});

// Helper function to get CSRF token from cookies
const getCsrfToken = () => {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name) {
      return value;
    }
  }
  return null;
};

// Request interceptor for adding CSRF token and handling auth
api.interceptors.request.use(
  (config) => {
    // When sending FormData, remove Content-Type so the browser sets multipart/form-data with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Add CSRF token for POST/PUT/DELETE requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors and auto-refreshing expired tokens
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Log error details for debugging
    if (error.response) {
      console.error('API Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: originalRequest?.url,
      });

      // On 401, attempt a silent token refresh once (_retry guards against infinite loops)
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await refreshApi.post('/auth/refresh/');
          // Retry the original request after successful refresh
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed — redirecting to login');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      if (error.response.status === 403) {
        localStorage.removeItem('user');
        // Redirect will be handled by component
      }
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;

